import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { useNotificationStore } from '@/stores/notificationStore';
import { apiFetch } from '@/lib/api';
import {
  comparison, flightCostService, fmtCost, fmtMinutes, fmtMoney, fmtStops, FlightCostError, UNPRICED_LABEL,
  type FcDestinationResults, type FcEstimate, type FcOriginRow, type FcOverview, type FcRun,
} from '@/services/FlightCostService';
import { RunStatusBadge } from './components/RunStatusBadge';
import { SpendingLimitsModal } from './components/SpendingLimitsModal';
import { fmtRange, fmtWhen } from './FlightCostEstimator';
import { AlertTriangle, ArrowLeft, Bot, Copy, Download, Link2, Pencil, RefreshCw, Search, Wallet, X } from 'lucide-react';
import { FlightCostAgentPanel, FLIGHT_COST_AGENT_ENABLED } from './agent/FlightCostAgentPanel';

type View = 'simple' | 'advanced';
const TH = 'text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500 whitespace-nowrap';
const TERMINAL: FcRun['status'][] = ['completed', 'failed', 'cancelled'];

export function FlightCostAssessment() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const [overview, setOverview] = useState<FcOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dest, setDest] = useState<FcDestinationResults | null>(null);
  const [rows, setRows] = useState<FcOriginRow[] | null>(null);
  const [view, setView] = useState<View>('simple');
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const pollRef = useRef<number | null>(null);

  const loadResults = useCallback(async () => {
    const [d, o] = await Promise.all([flightCostService.resultsByDestination(id), flightCostService.resultsByOrigin(id)]);
    setDest(d); setRows(o.rows);
  }, [id]);

  const load = useCallback(async () => {
    try {
      const ov = await flightCostService.overview(id);
      setOverview(ov);
      if (ov.last_run) await loadResults(); else { setDest(null); setRows(null); }
    } catch (e: any) { setLoadError(e?.message || 'Could not load this estimate'); }
  }, [id, loadResults]);

  useEffect(() => { load(); }, [load]);

  // Poll the active run until it finishes, then reload everything.
  const activeRun = overview?.last_run && !TERMINAL.includes(overview.last_run.status) ? overview.last_run : null;
  useEffect(() => {
    if (!activeRun) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const r = await flightCostService.run(id, activeRun.id);
        if (TERMINAL.includes(r.status)) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          if (r.status === 'completed') notify.success('Pricing finished');
          else if (r.status === 'failed') notify.error(r.error?.message || 'Pricing failed');
          await load();
        } else setOverview((ov) => (ov ? { ...ov, last_run: r } : ov));
      } catch { /* keep polling */ }
    }, 3000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [activeRun?.id]);

  if (loadError) return <PageContainer><div className="py-16 text-center text-sm text-gray-600">{loadError}</div></PageContainer>;
  if (!overview) return <PageContainer><div className="flex justify-center py-16"><Spinner /></div></PageContainer>;
  if (agentOpen && FLIGHT_COST_AGENT_ENABLED) {
    return <FlightCostAgentPanel eventId={overview.event.id} onClose={() => { setAgentOpen(false); load(); }} />;
  }

  const a = overview.assessment;
  const run = overview.last_run;
  const hasResults = !!dest && dest.run.status === 'completed';
  const blocking = overview.gaps.filter((g) => g.blocking);

  async function exportCsv() {
    const res = await apiFetch(flightCostService.exportUrl(id));
    if (!res.ok) return notify.error('Export failed');
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url; link.download = `flight-cost-${a.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`; link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <PageContainer>
      <button onClick={() => navigate('/tools/flight-cost')} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Flight Cost Estimates
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{a.name}</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            {overview.event.name} · {fmtRange(a.inputs.out_date, a.inputs.back_date)} · {cap(a.inputs.cabin)} · {overview.origins.total_passengers} traveller{overview.origins.total_passengers === 1 ? '' : 's'} from {overview.origins.airport_count} airport{overview.origins.airport_count === 1 ? '' : 's'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
            {run && <RunStatusBadge status={run.status} />}
            {run?.finished_at && <span>Priced {fmtWhen(run.finished_at)}{run.started_by_name ? ` by ${run.started_by_name}` : ''}{run.via_agent ? ' (via agent)' : ''}</span>}
            {run && <span>· Search cost {fmtCost(run.cost.spent_usd)}</span>}
            <span>· Basic fares {a.inputs.exclude_basic_fares ? 'excluded' : 'included'} · Average of the 5 cheapest fares</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setLimitsOpen(true)} leftIcon={<Wallet className="w-4 h-4" />}>Limits</Button>
          <Button variant="secondary" size="sm" disabled={!hasResults} onClick={exportCsv} leftIcon={<Download className="w-4 h-4" />}>Export CSV</Button>
          <Button variant="secondary" size="sm" disabled={!hasResults} onClick={() => setShareOpen(true)} leftIcon={<Link2 className="w-4 h-4" />}>Client link</Button>
          <Button size="sm" disabled={!!activeRun} onClick={() => setEstimateOpen(true)} leftIcon={<RefreshCw className="w-4 h-4" />}>
            {run ? 'Re-price' : 'Price'}
          </Button>
        </div>
      </div>

      {/* FLIGHT_COST_AGENT_SLOT (assessment page, Moniker Agents session): open the agent chat for this event. */}
      {FLIGHT_COST_AGENT_ENABLED && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => setAgentOpen(true)} leftIcon={<Bot className="w-4 h-4" />}>Ask Flight Cost Estimates</Button>
        </div>
      )}

      {activeRun && <RunProgress run={activeRun} />}
      {run?.status === 'failed' && !activeRun && (
        <Notice tone="error" title="The last pricing run failed">{run.error?.message || 'Unknown error'}{hasResults ? ' Showing the previous completed run.' : ''}</Notice>
      )}
      {overview.gaps.length > 0 && (
        <Notice tone={blocking.length ? 'error' : 'warning'} title={blocking.length ? 'Needs attention before pricing' : 'Heads up'}>
          <ul className="list-disc pl-4 space-y-0.5">{overview.gaps.map((g) => <li key={g.code}>{g.message}</li>)}</ul>
        </Notice>
      )}

      <OriginsSummary overview={overview} />

      {!run && !activeRun && (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-500">
          Not priced yet. Click <b>Price</b> to see the cost and start the fare searches.
        </div>
      )}

      {hasResults && dest && rows && (
        <>
          <div className="mt-6 flex items-center justify-between">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              {(['simple', 'advanced'] as View[]).map((v) => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                  {v === 'simple' ? 'Simple' : 'Advanced'}
                </button>
              ))}
            </div>
            <CurrencyNote dest={dest} />
          </div>
          {view === 'simple' ? <SimpleView dest={dest} /> : <AdvancedView assessmentId={id} rows={rows} dest={dest} onChanged={loadResults} />}
        </>
      )}

      <EstimateModal isOpen={estimateOpen} assessmentId={id} onClose={() => setEstimateOpen(false)} onOpenLimits={() => { setEstimateOpen(false); setLimitsOpen(true); }}
        onStarted={(r) => { setEstimateOpen(false); setOverview((ov) => (ov ? { ...ov, last_run: r } : ov)); }} />
      <ShareModal isOpen={shareOpen} assessmentId={id} onClose={() => setShareOpen(false)} />
      <SpendingLimitsModal isOpen={limitsOpen} onClose={() => setLimitsOpen(false)} onSaved={load} />
    </PageContainer>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

function Notice({ tone, title, children }: { tone: 'error' | 'warning' | 'info'; title: string; children: ReactNode }) {
  const c = tone === 'error' ? 'border-red-200 bg-red-50 text-red-800' : tone === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-blue-200 bg-blue-50 text-blue-900';
  return (
    <div className={`mt-4 rounded-lg border p-3 text-xs ${c}`}>
      <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle className="w-3.5 h-3.5" />{title}</div>
      <div>{children}</div>
    </div>
  );
}

function RunProgress({ run }: { run: FcRun }) {
  const total = run.counts?.searches_total || 0;
  const done = (run.counts?.searched || 0) + (run.counts?.cached || 0);
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between text-xs text-gray-700">
        <span className="flex items-center gap-2"><Spinner size="sm" /> {run.status === 'queued' ? 'Waiting to start…' : `Searching fares: ${done} of ${total}`}</span>
        <span className="text-gray-500">Estimated cost {fmtCost(run.cost.est_usd)}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-gray-800 transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function OriginsSummary({ overview }: { overview: FcOverview }) {
  const [open, setOpen] = useState(false);
  const o = overview.origins;
  const r = o.attendee_readiness;
  const missing = r ? r.unmatched.length + r.needs_city.length + r.no_service.length : 0;
  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs">
        <span className="font-medium text-gray-800">
          Travellers by home airport · {o.source === 'origins_list' ? 'from uploaded list' : 'from attendees'}
          {r && <span className="font-normal text-gray-500"> · {r.matched} matched{missing ? `, ${missing} without an airport` : ''}</span>}
        </span>
        <span className="text-gray-500">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="border-t overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50">{['Airport', 'City', 'Travellers', 'Max stops', ''].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {o.airports.map((x) => (
                <tr key={x.iata} className="border-b last:border-b-0 hover:bg-gray-100/50">
                  <td className="p-2 text-xs"><span className="font-mono font-semibold">{x.iata}</span> <span className="text-gray-500">{x.name}</span></td>
                  <td className="p-2 text-xs text-gray-700">{[x.city, x.country].filter(Boolean).join(', ')}</td>
                  <td className="p-2 text-xs">{x.passengers}</td>
                  <td className="p-2 text-xs">{x.max_stops === 0 ? 'Nonstop' : x.max_stops}</td>
                  <td className="p-2 text-xs">{x.overridden && <Badge variant="outline" className="text-[10px]">Changed by planner</Badge>}{x.status !== 'ok' && <Badge variant="warning" className="text-[10px]">No scheduled flights</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {r && missing > 0 && (
            <div className="border-t p-3 text-xs text-gray-600 space-y-1">
              {r.needs_city.length > 0 && <div><b>{r.needs_city.length}</b> with only a state or country: pick a city or airport for them.</div>}
              {r.unmatched.length > 0 && <div><b>{r.unmatched.length}</b> with no location match.</div>}
              {r.no_service.length > 0 && <div><b>{r.no_service.length}</b> nearest airport has no scheduled flights.</div>}
              <div className="text-gray-500">Ask the agent to fix these, e.g. "use YYZ for the Brampton travellers".</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CurrencyNote({ dest }: { dest: FcDestinationResults }) {
  const c = dest.currency;
  if (!c.event || c.event === c.original) return <span className="text-[11px] text-gray-500">Fares in USD, per person, return</span>;
  return <span className="text-[11px] text-gray-500">Per person, return · USD and {c.event}{c.fx_rate ? ` (1 USD = ${c.fx_rate.toFixed(4)} ${c.event}${c.fx_date ? `, ${c.fx_date.slice(0, 10)}` : ''})` : ''}</span>;
}

function TwoMoney({ m, ccy, big }: { m: { original: number | null; event: number | null } | null | undefined; ccy: string | null; big?: boolean }) {
  if (!m || m.original === null) return <span className="text-gray-400">—</span>;
  const showEvent = ccy && ccy !== 'USD' && m.event !== null;
  return (
    <span>
      <span className={big ? 'text-2xl font-semibold text-gray-900' : 'font-medium text-gray-900'}>{fmtMoney(m.original, 'USD')}</span>
      {showEvent && <span className={`${big ? 'block text-sm' : 'ml-1.5 text-[11px]'} text-gray-500`}>{fmtMoney(m.event, ccy)}</span>}
    </span>
  );
}

function SimpleView({ dest }: { dest: FcDestinationResults }) {
  const ccy = dest.currency.event;
  const { columns, rows } = comparison(dest);
  const multiAirport = dest.destinations.some((d) => d.airports.length > 1);
  return (
    <div className="mt-3 space-y-3">
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
        {columns.map((ap) => (
          <div key={ap.key} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-semibold text-gray-900">{ap.destLabel}</div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500"><span className="font-mono font-semibold text-gray-700">{ap.iata}</span> · {ap.city || ap.name}</div>
            <div className="mt-2"><TwoMoney m={ap.avg_price} ccy={ccy} big /></div>
            <div className="text-[11px] text-gray-500">average per traveller</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><div className="text-gray-500">Stops on the way</div><div className="font-medium">{fmtStops(ap.avg_out_stops)}</div></div>
              <div><div className="text-gray-500">Flight time there</div><div className="font-medium">{fmtMinutes(ap.avg_out_minutes)}</div></div>
            </div>
            <div className="mt-3 text-[11px] text-gray-500">
              {ap.priced_passengers} priced{ap.unpriced_passengers ? <span className="text-amber-700"> · {ap.unpriced_passengers} not priced</span> : ''}{ap.manual_routes ? ` · ${ap.manual_routes} manual` : ''}{ap.gap_filled_routes ? ` · ${ap.gap_filled_routes} from Google Flights` : ''}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className={TH}>City of origin</th><th className={TH}>Travellers</th>
              {columns.map((c) => <th key={c.key} className={TH}>{multiAirport ? `${c.destLabel} · ${c.iata}` : c.destLabel}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.city}|${r.country}`} className="border-b last:border-b-0 hover:bg-gray-100/50">
                <td className="p-2 text-xs whitespace-nowrap"><span className="text-gray-900">{r.city}</span>{r.country && <span className="text-gray-500">, {r.country}</span>} <span className="font-mono text-[10px] text-gray-400">{r.airport}</span></td>
                <td className="p-2 text-xs">{r.passengers}</td>
                {columns.map((c) => {
                  const cell = r.cells[c.key];
                  return (
                    <td key={c.key} className="p-2 text-xs whitespace-nowrap">
                      {cell?.price ? <><TwoMoney m={cell.price} ccy={ccy} /><div className="text-[10px] text-gray-400">{fmtStops(cell.outStops)} stops · {fmtMinutes(cell.outMinutes)} there</div></> : <span className="text-gray-400">Not priced</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type Editing = { key: string; price: string; reason: string } | null;

function AdvancedView({ assessmentId, rows, dest, onChanged }: { assessmentId: string; rows: FcOriginRow[]; dest: FcDestinationResults; onChanged: () => Promise<void> }) {
  const notify = useNotificationStore();
  const [destFilter, setDestFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'priced' | 'unpriced' | 'manual'>('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ccy = dest.currency.event;
  const destAirports = useMemo(() => [...new Set(rows.map((r) => r.dest_iata))], [rows]);

  const shown = rows.filter((r) =>
    (destFilter === 'all' || r.dest_iata === destFilter) &&
    (statusFilter === 'all' || r.status === statusFilter) &&
    (!q || [r.origin_iata, r.origin_city, r.origin_name].some((v) => v?.toLowerCase().includes(q.toLowerCase()))));
  const counts = { priced: rows.filter((r) => r.status === 'priced').length, unpriced: rows.filter((r) => r.status === 'unpriced').length, manual: rows.filter((r) => r.status === 'manual').length };

  async function saveOverride(r: FcOriginRow, price: number | null, reason: string) {
    setSaving(true);
    try {
      await flightCostService.setFareOverride(assessmentId, { origin_iata: r.origin_iata, dest_iata: r.dest_iata, price, currency: 'USD', reason });
      setEditing(null);
      await onChanged();
      notify.success(price === null ? 'Manual fare removed' : 'Manual fare saved');
    } catch (e: any) { notify.error(e?.message || 'Could not save the fare'); } finally { setSaving(false); }
  }

  const selectCls = 'h-8 text-xs rounded-md border border-gray-200 bg-white px-2 focus:outline-none focus:ring-1 focus:ring-gray-300';
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative w-56">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search origin" className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        <select value={destFilter} onChange={(e) => setDestFilter(e.target.value)} className={selectCls}>
          <option value="all">All destinations</option>
          {destAirports.map((d) => <option key={d} value={d}>To {d}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className={selectCls}>
          <option value="all">All routes ({rows.length})</option>
          <option value="priced">Priced ({counts.priced})</option>
          <option value="unpriced">Not priced ({counts.unpriced})</option>
          <option value="manual">Manual fare ({counts.manual})</option>
        </select>
        <span className="text-[11px] text-gray-500 ml-auto">Click a fare to set it by hand. Click a row for the fares used.</span>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              {['Origin', 'Pax', 'Max stops', 'To', 'Fare', 'Stops there', 'Time there', 'Round trip', 'Fares used', 'Basic excl.', 'Source', 'Status'].map((h) => <th key={h} className={TH}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const key = `${r.origin_iata}|${r.dest_iata}`;
              const isEditing = editing?.key === key;
              return (
                <Fragment key={key}>
                  <tr className="group border-b hover:bg-gray-100/50 cursor-pointer" onClick={() => setExpanded(expanded === key ? null : key)}>
                    <td className="p-2 text-xs whitespace-nowrap"><span className="font-mono font-semibold">{r.origin_iata}</span> <span className="text-gray-500">{r.origin_city}</span></td>
                    <td className="p-2 text-xs">{r.passengers}</td>
                    <td className="p-2 text-xs">{r.max_stops === 0 ? 'Nonstop' : r.max_stops}</td>
                    <td className="p-2 text-xs font-mono">{r.dest_iata}</td>
                    <td className="p-2 text-xs whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input autoFocus type="number" min={1} value={editing!.price} onChange={(e) => setEditing({ ...editing!, price: e.target.value })} placeholder="USD"
                            className="w-20 h-7 px-1.5 text-xs rounded border border-gray-300" />
                          <input value={editing!.reason} onChange={(e) => setEditing({ ...editing!, reason: e.target.value })} placeholder="Reason"
                            onKeyDown={(e) => { if (e.key === 'Enter' && Number(editing!.price) > 0 && editing!.reason.trim()) saveOverride(r, Number(editing!.price), editing!.reason.trim()); if (e.key === 'Escape') setEditing(null); }}
                            className="w-36 h-7 px-1.5 text-xs rounded border border-gray-300" />
                          <Button size="sm" disabled={saving || !(Number(editing!.price) > 0) || !editing!.reason.trim()} onClick={() => saveOverride(r, Number(editing!.price), editing!.reason.trim())}>Save</Button>
                          <button onClick={() => setEditing(null)} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <button className="inline-flex items-center gap-1 hover:underline" onClick={() => setEditing({ key, price: r.manual_override ? String(r.manual_override.price) : '', reason: r.manual_override?.reason || '' })}>
                          {r.status === 'unpriced' ? <span className="text-gray-400">Set fare</span> : <TwoMoney m={r.price} ccy={ccy} />}
                          <Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100" />
                        </button>
                      )}
                    </td>
                    <td className="p-2 text-xs">{fmtStops(r.out_stops)}</td>
                    <td className="p-2 text-xs whitespace-nowrap">{fmtMinutes(r.out_minutes)}</td>
                    <td className="p-2 text-[11px] text-gray-500 whitespace-nowrap" title="Both flights added together, average stops per direction">{fmtMinutes(r.minutes)}{r.stops !== null ? ` · ${fmtStops(r.stops)} stops` : ''}</td>
                    <td className="p-2 text-xs">{r.offers_used.length ? `${r.offers_used.length} of ${r.offers_considered}` : '—'}</td>
                    <td className="p-2 text-xs">{r.basic_excluded || '—'}</td>
                    <td className="p-2 text-[11px] text-gray-500 whitespace-nowrap">{sourceLabel(r.source)}{r.cached ? ' · cached' : ''}</td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {r.status === 'priced' && <Badge variant="success" className="text-[10px]">Priced</Badge>}
                      {r.status === 'manual' && <Badge variant="primary" className="text-[10px]" title={r.manual_override?.reason}>Manual{r.manual_override?.via_agent ? ' (agent)' : ''}</Badge>}
                      {r.status === 'unpriced' && <Badge variant="warning" className="text-[10px]">{UNPRICED_LABEL[r.unpriced_reason || ''] || 'Not priced'}</Badge>}
                    </td>
                  </tr>
                  {expanded === key && (
                    <tr className="border-b bg-gray-50/60">
                      <td colSpan={12} className="p-3">
                        {r.manual_override && (
                          <div className="mb-2 flex items-center gap-2 text-xs text-gray-700">
                            Manual fare {fmtMoney(r.manual_override.price, r.manual_override.currency)}: {r.manual_override.reason}
                            <Button variant="ghost" size="sm" disabled={saving} onClick={() => saveOverride(r, null, 'Removed manual fare')}>Remove</Button>
                          </div>
                        )}
                        {r.offers_used.length === 0 ? <div className="text-xs text-gray-500">No fares were used for this route.</div> : (
                          <table className="text-xs">
                            <thead><tr>{['Fare', 'Airline', 'Stops (there / back)', 'Time there', 'Round trip', 'Fare type'].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                            <tbody>
                              {r.offers_used.map((o) => (
                                <tr key={o.id}><td className="px-2 py-1">{fmtMoney(o.amount, o.currency)}</td><td className="px-2 py-1">{o.carrier}</td><td className="px-2 py-1">{o.stops.join(' / ')}</td><td className="px-2 py-1">{fmtMinutes(o.outMinutes)}</td><td className="px-2 py-1">{fmtMinutes(o.minutes)}</td><td className="px-2 py-1 text-gray-500">{o.brand || '—'}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {r.fetched_at && <div className="mt-2 text-[11px] text-gray-400">Fetched {fmtWhen(r.fetched_at)}</div>}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-xs text-gray-500">No routes match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const sourceLabel = (s: string | null) => (s === 'duffel' ? 'Duffel' : s === 'serpapi' ? 'Google Flights (basic not excluded)' : s === 'mock' ? 'Test data' : s || '—');

function EstimateModal({ isOpen, assessmentId, onClose, onStarted, onOpenLimits }: { isOpen: boolean; assessmentId: string; onClose: () => void; onStarted: (r: FcRun) => void; onOpenLimits: () => void }) {
  const notify = useNotificationStore();
  const [est, setEst] = useState<FcEstimate | null>(null);
  const [err, setErr] = useState<FlightCostError | null>(null);
  const [starting, setStarting] = useState(false);
  const keyRef = useRef<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setEst(null); setErr(null); keyRef.current = crypto.randomUUID();
    flightCostService.estimate(assessmentId).then(setEst).catch((e) => setErr(e));
  }, [isOpen, assessmentId]);

  async function start() {
    setStarting(true);
    try { onStarted(await flightCostService.startRun(assessmentId, keyRef.current)); notify.success('Pricing started'); }
    catch (e: any) { setErr(e); } finally { setStarting(false); }
  }

  const overCap = est && !est.within_caps;
  const blocked = !!est?.blocking_gaps.length;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Price this estimate" size="sm"
      footer={<div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {overCap || err?.code === 'CAP_EXCEEDED' ? <Button onClick={onOpenLimits}>Change limits</Button>
          : <Button onClick={start} loading={starting} disabled={!est || blocked}>Start pricing</Button>}
      </div>}>
      {!est && !err && <div className="flex justify-center py-6"><Spinner /></div>}
      {err && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 mb-3">{err.message}</div>}
      {est && (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Routes to search" value={`${est.searches_to_run}`} sub={est.searches_cached ? `${est.searches_cached} already cached (free)` : undefined} />
            <Stat label="Estimated cost" value={fmtCost(est.est_cost_usd)} sub={`Fare source: ${sourceLabel(est.provider)}`} />
            <Stat label="Per run limit" value={fmtCost(est.per_run_cap_usd)} />
            <Stat label="Left this month" value={fmtCost(est.monthly_remaining_usd)} />
          </div>
          <div className="text-xs text-gray-600">
            {est.plan.passengers} travellers · {est.plan.origins} home airports × {est.plan.destination_airports} destination airports · {fmtRange(est.plan.out_date, est.plan.back_date)} · {cap(est.plan.cabin)}
          </div>
          {overCap && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">This run would go over the spending limit. Raise the limit to continue.</div>}
          {blocked && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"><ul className="list-disc pl-4">{est.blocking_gaps.map((g) => <li key={g.code}>{g.message}</li>)}</ul></div>}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-gray-200 p-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-base font-semibold text-gray-900">{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

function ShareModal({ isOpen, assessmentId, onClose }: { isOpen: boolean; assessmentId: string; onClose: () => void }) {
  const notify = useNotificationStore();
  const [link, setLink] = useState<{ url: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try { setLink(await flightCostService.createShareLink(assessmentId)); } catch (e: any) { notify.error(e?.message || 'Could not create the link'); } finally { setBusy(false); }
  }
  useEffect(() => { if (isOpen) { setLink(null); create(); } }, [isOpen]);

  async function disable() {
    setBusy(true);
    try { await flightCostService.deleteShareLink(assessmentId); notify.success('Link turned off'); onClose(); } catch (e: any) { notify.error(e?.message || 'Could not turn off the link'); } finally { setBusy(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Client link" size="sm"
      description="Shows the average fare per destination and per city of origin, with stops and travel time. Never shows traveller names or search costs."
      footer={<div className="flex justify-between w-full"><Button variant="danger" size="sm" onClick={disable} disabled={busy || !link}>Turn off link</Button><Button variant="ghost" onClick={onClose}>Close</Button></div>}>
      {!link ? <div className="flex justify-center py-4"><Spinner /></div> : (
        <div className="flex items-center gap-2">
          <input readOnly value={link.url} className="flex-1 h-8 px-2 text-xs rounded-md border border-gray-200 bg-gray-50" onFocus={(e) => e.currentTarget.select()} />
          <Button size="sm" variant="secondary" leftIcon={<Copy className="w-3.5 h-3.5" />} onClick={() => { navigator.clipboard.writeText(link.url); notify.success('Link copied'); }}>Copy</Button>
          <a href={link.url} target="_blank" rel="noreferrer" className="text-xs text-gray-600 hover:underline">Open</a>
        </div>
      )}
    </Modal>
  );
}
